// File: src/pages/About/About.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Upload, 
  Brain, 
  Search, 
  Calculator, 
  MapPin, 
  CheckCircle,
  Zap,
  Eye,
  DollarSign,
  Shield,
  Clock,
  Users,
  TrendingUp,
  AlertTriangle,
  Target,
  Award,
  ArrowRight
} from 'lucide-react';
import styles from './About.module.css';

const About = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [activeFAQ, setActiveFAQ] = useState(null);

  // How it works steps
  const steps = [
    {
      id: 'upload',
      icon: Upload,
      title: 'Upload Your Policy',
      description: 'Drop your insurance document - PDF, photo, screenshot. We accept all formats.',
      details: 'Our AI can read any insurance document format. Just drag and drop your policy, and we\'ll extract every important detail automatically.',
      color: 'var(--theme-primary)',
      time: '5 seconds'
    },
    {
      id: 'scan',
      icon: Eye,
      title: 'AI Document Analysis',
      description: 'Advanced AI scans every line, extracting coverage details, premiums, and deductibles.',
      details: 'We use cutting-edge OCR and machine learning to understand your policy better than most humans. No detail is missed.',
      color: 'var(--theme-secondary)',
      time: '10 seconds'
    },
    {
      id: 'compare',
      icon: Search,
      title: 'Massive Database Comparison',
      description: 'Compare against 47,329+ real policies from your area.',
      details: 'Our database includes real anonymized policies from across Canada. We compare apples-to-apples for accurate results.',
      color: 'var(--theme-accent)',
      time: '15 seconds'
    },
    {
      id: 'calculate',
      icon: Calculator,
      title: 'Grade Calculation',
      description: 'Sophisticated algorithm determines your A-F grade.',
      details: 'We consider 50+ factors including coverage amounts, deductibles, location, demographics, and market rates.',
      color: 'var(--theme-primary)',
      time: '5 seconds'
    },
    {
      id: 'reveal',
      icon: Award,
      title: 'Results & Recommendations',
      description: 'Get your grade and see exactly how much you could save.',
      details: 'Discover if you\'re getting crushed or crushing it, plus actionable recommendations to improve your situation.',
      color: 'var(--theme-secondary)',
      time: 'Instant'
    }
  ];

  // Stats for social proof
  const stats = [
    { icon: Users, value: '47,329+', label: 'Policies Analyzed' },
    { icon: DollarSign, value: '$2.3M', label: 'Saved This Month' },
    { icon: TrendingUp, value: '73%', label: 'Users Overpaying' },
    { icon: Target, value: '$540', label: 'Average Savings' }
  ];

  // FAQ data
  const faqs = [
    {
      question: 'How accurate are your comparisons?',
      answer: 'Our AI analyzes real policies from 47,329+ Canadians. We compare identical coverage types, amounts, and deductibles to ensure accuracy. Our predictions are typically within 5% of actual market rates.'
    },
    {
      question: 'Is my personal information secure?',
      answer: 'Absolutely. Your policy documents are encrypted in transit and at rest. We automatically delete all uploaded files within 24 hours. We never store personal information like your name, address, or policy numbers.'
    },
    {
      question: 'What insurance types do you support?',
      answer: 'We currently analyze auto, home, condo, and tenant insurance policies from major Canadian insurers. We\'re constantly adding support for more insurance types and providers.'
    },
    {
      question: 'How do you make money?',
      answer: 'Cover Crush is completely free for consumers. We earn commissions when users choose to switch to better policies through our partner network. You never pay us directly.'
    },
    {
      question: 'Can I get quotes for better rates?',
      answer: 'Yes! If your grade shows you\'re overpaying, we can connect you with our partner brokers who specialize in finding better rates for your specific situation.'
    },
    {
      question: 'How recent is your data?',
      answer: 'Our database is updated daily with new policy data. Market rates and trends are refreshed every week to ensure you get the most current comparisons.'
    }
  ];

  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };

  return (
    <div className={styles.aboutPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>
              How Cover Crush
              <span className={styles.heroAccent}>Actually Works</span>
            </h1>
            <p className={styles.heroSubtitle}>
              The brutal truth about your insurance rates, revealed in 30 seconds by advanced AI analysis.
            </p>
            <div className={styles.heroStats}>
              {stats.map((stat, index) => (
                <div key={index} className={styles.statItem}>
                  <stat.icon className={styles.statIcon} />
                  <div className={styles.statValue}>{stat.value}</div>
                  <div className={styles.statLabel}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className={styles.howItWorksSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>The 30-Second Analysis Process</h2>
          <p className={styles.sectionSubtitle}>
            See exactly how we crush the competition and reveal the truth about your premiums
          </p>
          
          <div className={styles.stepsContainer}>
            <div className={styles.stepsNavigation}>
              {steps.map((step, index) => (
                <button
                  key={step.id}
                  className={`${styles.stepNavButton} ${activeStep === index ? styles.active : ''}`}
                  onClick={() => setActiveStep(index)}
                >
                  <div className={styles.stepNavIcon} style={{ color: step.color }}>
                    <step.icon size={24} />
                  </div>
                  <div className={styles.stepNavContent}>
                    <div className={styles.stepNavTitle}>{step.title}</div>
                    <div className={styles.stepNavTime}>{step.time}</div>
                  </div>
                </button>
              ))}
            </div>

            <div className={styles.stepDisplay}>
              <div className={styles.stepContent}>
                <div 
                  className={styles.stepIcon} 
                  style={{ backgroundColor: steps[activeStep].color }}
                >
                  {React.createElement(steps[activeStep].icon, { size: 32 })}
                </div>
                <h3 className={styles.stepTitle}>{steps[activeStep].title}</h3>
                <p className={styles.stepDescription}>{steps[activeStep].description}</p>
                <div className={styles.stepDetails}>
                  <p>{steps[activeStep].details}</p>
                </div>
                <div className={styles.stepTime}>
                  <Clock size={16} />
                  <span>Takes {steps[activeStep].time}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Trust Us Section */}
      <section className={styles.trustSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Why Trust Cover Crush?</h2>
          
          <div className={styles.trustGrid}>
            <div className={styles.trustCard}>
              <div className={styles.trustIcon}>
                <Brain />
              </div>
              <h3 className={styles.trustTitle}>Advanced AI Technology</h3>
              <p className={styles.trustDescription}>
                Our machine learning models are trained on millions of insurance documents. 
                We understand policy language better than most humans.
              </p>
            </div>

            <div className={styles.trustCard}>
              <div className={styles.trustIcon}>
                <Shield />
              </div>
              <h3 className={styles.trustTitle}>Bank-Level Security</h3>
              <p className={styles.trustDescription}>
                256-bit encryption, automatic file deletion, and zero personal data storage. 
                Your privacy is our top priority.
              </p>
            </div>

            <div className={styles.trustCard}>
              <div className={styles.trustIcon}>
                <Users />
              </div>
              <h3 className={styles.trustTitle}>Real Data, Real Results</h3>
              <p className={styles.trustDescription}>
                47,329+ real policies from real Canadians. No fake data, no inflated numbers. 
                Just honest comparisons.
              </p>
            </div>

            <div className={styles.trustCard}>
              <div className={styles.trustIcon}>
                <CheckCircle />
              </div>
              <h3 className={styles.trustTitle}>100% Free Forever</h3>
              <p className={styles.trustDescription}>
                No hidden fees, no spam, no BS. We make money when you save money. 
                Your analysis is always free.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Frequently Asked Questions</h2>
          
          <div className={styles.faqList}>
            {faqs.map((faq, index) => (
              <div key={index} className={styles.faqItem}>
                <button 
                  className={`${styles.faqQuestion} ${activeFAQ === index ? styles.active : ''}`}
                  onClick={() => toggleFAQ(index)}
                >
                  <span>{faq.question}</span>
                  <div className={styles.faqToggle}>
                    {activeFAQ === index ? '−' : '+'}
                  </div>
                </button>
                {activeFAQ === index && (
                  <div className={styles.faqAnswer}>
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>Ready to Discover the Truth?</h2>
            <p className={styles.ctaSubtitle}>
              Join 47,329+ Canadians who found out if they're getting crushed by their insurance rates.
            </p>
            <Link to="/" className={styles.ctaButton}>
              <Upload size={20} />
              Analyze My Policy Now
              <ArrowRight size={20} />
            </Link>
            <div className={styles.ctaNote}>
              <Shield size={16} />
              <span>Free analysis • No signup required • Secure & private</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;